'use client'

import { Button } from '@/ui/atoms/button/Button'
import { Panel } from '@/ui/atoms/panel/Panel'
import { Typography } from '@/ui/atoms/typography/Typography'
import { cn } from '@/ui/utils/style'
import { AlertTriangle, CheckCircle2, Loader2, ShieldOff, XCircle } from 'lucide-react'
import { SUPPORT_URLS } from '../constants'
import { type CardDelegationInfo, useRevokeDelegations } from '../hooks/useRevokeDelegations'
import { useUserCards } from '../hooks/useUserCards'
import type { AccountStatus } from '../types/CardState'

interface CardAccountSuspendedProps {
  accountStatus?: AccountStatus
}

function DelegationRow({
  delegation,
  isRevoking,
  revokingKey,
  onRevoke,
}: {
  delegation: CardDelegationInfo
  isRevoking: boolean
  revokingKey: string | null
  onRevoke: (key: string) => void
}) {
  const isThisRevoking = revokingKey === delegation.revokeKey
  const wasRevoked = !delegation.isActive && !delegation.isLoading

  return (
    <div className="flex items-center justify-between rounded-lg border border-border/50 bg-background/30 px-4 py-3">
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <Typography variant="p" className="font-medium text-sm">
            {delegation.displayName}
          </Typography>
          <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
            •••• {delegation.last4}
          </span>
        </div>
        <Typography variant="p" className="text-muted-foreground text-xs">
          {delegation.delegationType === 'euler' || delegation.delegationType === 'euler-withdraw'
            ? 'EVC Operator'
            : delegation.delegationType === 'pooled'
              ? 'Borrow Delegation'
              : 'Spend Allowance'}
        </Typography>
      </div>

      {delegation.isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      ) : wasRevoked ? (
        <div className="flex items-center gap-1.5 text-green-400">
          <CheckCircle2 className="h-4 w-4" />
          <span className="text-xs">Revoked</span>
        </div>
      ) : delegation.isActive ? (
        <Button
          size="sm"
          variant="outline"
          onClick={() => onRevoke(delegation.revokeKey)}
          disabled={isRevoking}
          className="border-red-500/30 text-red-400 hover:border-red-500/50 hover:bg-red-500/10"
        >
          {isThisRevoking ? <Loader2 className="mr-1.5 h-3 w-3 animate-spin" /> : null}
          {isThisRevoking ? 'Revoking...' : 'Revoke'}
        </Button>
      ) : (
        <span className="text-muted-foreground text-xs">Inactive</span>
      )}
    </div>
  )
}

export function CardAccountSuspended({ accountStatus }: CardAccountSuspendedProps) {
  const isFrozen = accountStatus === 'frozen'
  const { cards } = useUserCards()
  const { delegations, isLoading, isRevoking, revokingKey, revokeError, revoke } = useRevokeDelegations(cards)

  const activeDelegations = delegations.filter((d) => d.isActive)
  const allRevoked = !isLoading && delegations.length > 0 && activeDelegations.length === 0

  return (
    <div className="flex flex-col items-center gap-12 lg:flex-row lg:items-start">
      <div className="flex flex-1 flex-col gap-6">
        <Panel.Wrapper className="relative w-full max-w-xl overflow-hidden">
          <div
            className={cn(
              'pointer-events-none absolute inset-0 rounded-lg bg-gradient-to-br via-transparent',
              isFrozen ? 'from-amber-500/10 to-amber-400/5' : 'from-red-500/10 to-red-400/5',
            )}
          />

          <Panel.Content className="relative flex flex-col gap-6 p-8">
            <div className="flex items-center gap-3">
              <div
                className={cn(
                  'flex h-12 w-12 items-center justify-center rounded-full border-2',
                  isFrozen ? 'border-amber-500/30 bg-amber-500/10' : 'border-red-500/30 bg-red-500/10',
                )}
              >
                {isFrozen ? (
                  <AlertTriangle className="h-6 w-6 text-amber-400" />
                ) : (
                  <ShieldOff className="h-6 w-6 text-red-400" />
                )}
              </div>
              <div>
                <Typography variant="h3" className={isFrozen ? 'text-amber-400' : 'text-red-400'}>
                  {isFrozen ? 'Account Frozen' : 'Account Revoked'}
                </Typography>
              </div>
            </div>

            <Typography variant="p" className="text-muted-foreground">
              {isFrozen
                ? 'Your card account has been temporarily frozen. Card operations are disabled, but you can revoke any active onchain delegations below.'
                : 'Your card account has been permanently revoked. You should revoke any remaining onchain delegations to secure your positions.'}
            </Typography>

            {delegations.length > 0 && (
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <Typography variant="p" className="font-medium text-sm">
                    Active Delegations
                  </Typography>
                  {allRevoked && (
                    <div className="flex items-center gap-1.5 text-green-400">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      <span className="text-xs">All revoked</span>
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  {delegations.map((delegation) => (
                    <DelegationRow
                      key={delegation.revokeKey}
                      delegation={delegation}
                      isRevoking={isRevoking}
                      revokingKey={revokingKey}
                      onRevoke={revoke}
                    />
                  ))}
                </div>
              </div>
            )}

            {isLoading && delegations.length === 0 && (
              <div className="flex items-center justify-center gap-2 py-4">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                <Typography variant="p" className="text-muted-foreground text-sm">
                  Checking delegations...
                </Typography>
              </div>
            )}

            {!isLoading && delegations.length === 0 && (
              <div className="rounded-lg border border-border/50 bg-background/30 px-4 py-3">
                <Typography variant="p" className="text-center text-muted-foreground text-sm">
                  No active delegations found
                </Typography>
              </div>
            )}

            {revokeError && (
              <div className="flex items-center gap-2 rounded-lg border border-red-500/20 bg-red-500/10 px-4 py-3">
                <XCircle className="h-4 w-4 shrink-0 text-red-400" />
                <Typography variant="p" className="text-red-400 text-sm">
                  {revokeError.message}
                </Typography>
              </div>
            )}

            <Button
              onClick={() => window.open(SUPPORT_URLS.KYC_SUPPORT, '_blank')}
              variant="outline"
              size="lg"
              className="w-full"
            >
              Contact Support
            </Button>
          </Panel.Content>
        </Panel.Wrapper>
      </div>
    </div>
  )
}
